import { useEffect, useState, type ReactNode } from 'react';
import { API_ROUTES, type SettingsResponse } from '@oscom/shared';
import { apiGet, ApiRequestError } from '../api/client';
import { useSession } from '../auth/SessionProvider';
import { Layout } from '../components/Layout';

/**
 * Protected dashboard - the landing page after sign-in.
 *
 * It shows what actually exists (identity, server resolved role, backend
 * connectivity) and is explicit about what does not exist yet. The areas that
 * do work are reached through the header navigation, not from here.
 */
export function DashboardPage(): ReactNode {
  const { session } = useSession();
  const [settings, setSettings] = useState<SettingsResponse | undefined>(undefined);
  const [settingsError, setSettingsError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    apiGet<SettingsResponse>(API_ROUTES.settings)
      .then((response) => {
        if (!cancelled) {
          setSettings(response);
        }
      })
      .catch((error: unknown) => {
        if (cancelled) {
          return;
        }
        setSettingsError(
          error instanceof ApiRequestError
            ? error.message
            : 'Einstellungen konnten nicht geladen werden.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (session?.status !== 'authenticated') {
    return null;
  }
  const { me } = session;

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        <div className="card">
          <h1>Willkommen, {me.displayName}</h1>
          <p className="muted">
            Angemeldet ueber Microsoft Entra ID. Rolle und Berechtigungen werden serverseitig
            bestimmt.
          </p>
          <dl className="detail-grid">
            <div>
              <dt>Rolle</dt>
              <dd>
                <span className="badge">{me.role}</span>{' '}
                <span className="muted small">({roleSourceLabel(me.roleSource)})</span>
              </dd>
            </div>
            <div>
              <dt>Benutzerkennung</dt>
              <dd>
                <code className="selectable">{me.userId}</code>
                <span className="muted small">
                  {' '}
                  wird fuer die Rollenzuweisung durch die Administration benoetigt
                </span>
              </dd>
            </div>
            <div>
              <dt>Anmeldeverfahren</dt>
              <dd>{me.identityProvider === 'aad' ? 'Microsoft Entra ID' : me.identityProvider}</dd>
            </div>
            <div>
              <dt>Serverzeit der Pruefung</dt>
              <dd>{formatUtc(me.checkedAtUtc)}</dd>
            </div>
          </dl>
        </div>

        <div className="card">
          <h2>Berechtigungen</h2>
          <ul className="chip-list">
            {me.permissions.map((permission) => (
              <li className="chip" key={permission}>
                {permission}
              </li>
            ))}
          </ul>
        </div>

        <div className="card">
          <h2>Naechste Ausbaustufen</h2>
          <p className="notice notice--info">
            Verfuegbar sind Anmeldung, Rollen und die Kundenverwaltung. Ortsstammdaten,
            Dokumentationseditor, Bild- und Anhangsverwaltung, PDF-Export sowie Kundenfreigaben sind
            noch nicht Bestandteil dieser Version.
          </p>
          {settingsError ? (
            <p className="notice notice--warning">{settingsError}</p>
          ) : (
            <ul className="roadmap">
              {(settings?.plannedCapabilities ?? []).map((capability) => (
                <li key={capability}>
                  <span className="roadmap__state">geplant</span>
                  {capability}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card">
          <h2>Offene Fachfragen</h2>
          <p>
            Der Fragenkatalog umfasst 33 fachliche Festlegungen &ndash; von den Grenzwerten der
            Ampelbewertung bis zum Schema der Geraetebezeichnungen. 28 davon sind beantwortet. Die
            fuenf offenen betreffen die Nummerierung der Messpunkte nach dem Geraetewechsel und das
            Format der Messdaten aus dem neuen Geraet.
          </p>
          <p>
            <a href="/fragen">Fragenkatalog oeffnen und beantworten</a>
          </p>
          <p className="muted small">
            Antworten werden serverseitig gespeichert und wandern ueber einen taeglichen Lauf
            zurueck ins Repository.
          </p>
        </div>

        {settings ? (
          <p className="muted small">
            {settings.platformName} &middot; Umgebung: {settings.environment} &middot; Phase{' '}
            {settings.phase} &middot; {settings.confidentialityNotice}
          </p>
        ) : null}
      </section>
    </Layout>
  );
}

function roleSourceLabel(source: string): string {
  switch (source) {
    case 'assignment':
      return 'explizit zugewiesen';
    case 'bootstrapAdmin':
      return 'Bootstrap-Administrator';
    default:
      return 'Standardrolle';
  }
}

function formatUtc(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return `${parsed.toISOString().replace('T', ' ').slice(0, 19)} UTC`;
}
